import { m as motion } from "framer-motion";
import { Check, Users, Zap } from "lucide-react";
import { fadeInUp, defaultViewport, defaultTransition } from "@/lib/motion";
import { CallToActionSection } from "@/components/call-to-action-section";
import { useSEO } from "@/lib/use-seo";
import { breadcrumb } from "@/lib/seo-schemas";

const teamPrograms = [
  {
    name: "Private AI for HR Mastermind",
    icon: <Users className="w-8 h-8" />,
    description: "Tailored specifically for your team with use cases focused on your organization's priorities. We'll help each team member create a custom AI assistant that demonstrates the real impact people who know how to leverage AI can have at your organization.",
    features: [
      "Train your whole people team",
      "Organization-specific use cases and implementation plans",
      "Custom AI assistants built for your specific HR challenges",
      "Private sessions and dedicated support for your team",
      "Practical implementation strategies using your actual data and workflows",
    ],
  },
  {
    name: "People-first AI Launchpad",
    icon: <Zap className="w-8 h-8" />,
    description: "Accelerate your organization's AI adoption with a comprehensive program that combines cohort-based training, strategic workshops, and hands-on implementation.",
    features: [
      "Private cohort-based training with your AI champions",
      "AI strategy workshop with leaders",
      "Course content customized for your business",
      "Ends with an all-hands AI experiment demo",
    ],
  },
];

export const TeamTraining = () => {
  useSEO({
    title: "AI for HR Team Training",
    description: "Custom AI training programs for HR teams. Private cohorts, organization-specific use cases, and hands-on implementation with dedicated facilitators.",
    path: "/team-training",
    jsonLd: breadcrumb([
      { name: "Home", path: "/" },
      { name: "Team Training", path: "/team-training" },
    ]),
  });
  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-30 pointer-events-none" />
        <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="section-inner relative z-10">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={defaultViewport}
            transition={defaultTransition}
            className="text-center space-y-4 max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-4">
              Enterprise Solutions
            </span>
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none bg-clip-text text-transparent bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))]">
              AI for HR Mastermind for Teams
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Transform your entire HR team's approach to AI with custom training programs designed specifically for your organization's needs and priorities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs */}
      <section className="section-inner pb-20">
        <div className="grid gap-8 lg:grid-cols-2 max-w-6xl mx-auto">
          {teamPrograms.map((program, index) => (
            <motion.div
              key={program.name}
              initial={fadeInUp.initial}
              whileInView={fadeInUp.animate}
              viewport={defaultViewport}
              transition={{ ...defaultTransition, delay: index * 0.1 }}
              className="glass-card p-8 hover-lift border-t-2 border-primary/30 flex flex-col"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6">
                {program.icon}
              </div>
              <h3 className="text-2xl font-bold mb-3">{program.name}</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {program.description}
              </p>

              <ul className="space-y-3 flex-grow mb-8">
                {program.features.map((feature, fi) => (
                  <li key={fi} className="flex gap-3">
                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                <a
                  href="mailto:info@workplacelabs.io"
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-primary hover:shadow-glow-primary hover:-translate-y-0.5 transition-all duration-300"
                >
                  Request Information
                </a>
                <a
                  href="https://cal.com/jonathanflower/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center justify-center rounded-xl glass hover:glass-strong px-6 text-sm font-medium transition-all duration-300"
                >
                  Schedule a Call
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <CallToActionSection />
    </div>
  );
};

export default TeamTraining;
